import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useMutation } from "@apollo/client";
import { Elements } from "@stripe/react-stripe-js";
import { Alert, Button, Typography } from "@mui/material";
import type { Appearance, Stripe } from "@stripe/stripe-js";
import type {
  BeginPaymentMethodSetupInput,
  BeginPaymentMethodSetupPayload,
} from "types";
import { BEGIN_PAYMENT_METHOD_SETUP_MUTATION } from "globals/graphql/paymentMethod.graphql";
import SetupElement from "./SetupElement";
import type {
  PaymentMethodSetupHandle,
  PaymentMethodSetupProps,
  SavedPaymentMethod,
  SetupAttempt,
} from "./types";

type Props = PaymentMethodSetupProps & {
  storageScope: string;
  appearance: Appearance;
  stripePromise: Promise<Stripe | null>;
  onRetry: () => void;
};

const SavedCardSession = forwardRef<PaymentMethodSetupHandle, Props>(
  (props, ref) => {
    const [begin] = useMutation<
      { beginPaymentMethodSetup: BeginPaymentMethodSetupPayload },
      { input: BeginPaymentMethodSetupInput }
    >(BEGIN_PAYMENT_METHOD_SETUP_MUTATION);
    const [attempt, setAttempt] = useState<SetupAttempt>();
    const [error, setError] = useState<string>();
    const selectedRef = useRef<SavedPaymentMethod>();
    const elementRef = useRef<PaymentMethodSetupHandle>(null);
    const activeRef = useRef(true);
    const readyRef = useRef(false);
    const propsRef = useRef(props);
    propsRef.current = props;

    useEffect(() => {
      activeRef.current = true;
      const controller = new AbortController();
      const timer = setTimeout(() => {
        controller.abort();
        if (activeRef.current)
          setError("Saved cards took too long to load. Please try again.");
      }, 30000);
      propsRef.current.onStateChange({
        status: "initializing",
        ready: false,
        complete: false,
        busy: true,
        error: null,
        result: null,
      });
      const initialize = async () => {
        try {
          // A new Customer Session also refreshes the list after standalone Add Card.
          const { data } = await begin({
            variables: {
              input: {
                ...propsRef.current.context,
                attemptKey: propsRef.current.attemptKey,
              },
            },
            context: { fetchOptions: { signal: controller.signal } },
          });
          if (!activeRef.current || controller.signal.aborted) return;
          const result = data?.beginPaymentMethodSetup?.result;
          if (result?.__typename !== "BeginPaymentMethodSetupSuccess") {
            throw new Error(result?.reason || "Unable to load saved cards.");
          }
          if (
            propsRef.current.savedPaymentMethods?.length &&
            !result.customerSessionClientSecret
          ) {
            throw new Error("Unable to load saved cards. Please try again.");
          }
          setAttempt(result);
          propsRef.current.onAttemptCreated?.(result);
        } catch (failure) {
          if (activeRef.current && !controller.signal.aborted) {
            setError(
              failure instanceof Error
                ? failure.message
                : "Unable to load saved cards."
            );
          }
        } finally {
          clearTimeout(timer);
        }
      };
      initialize();
      return () => {
        activeRef.current = false;
        controller.abort();
        clearTimeout(timer);
      };
    }, [begin]);

    useEffect(() => {
      if (error)
        propsRef.current.onStateChange({
          status: "failed",
          ready: false,
          complete: false,
          busy: false,
          error,
          result: null,
        });
    }, [error]);

    useImperativeHandle(
      ref,
      () => ({
        save: async (context) => {
          if (!activeRef.current || !readyRef.current || !elementRef.current) {
            throw new Error("Card form is still loading. Please try again.");
          }
          const selected = selectedRef.current;
          if (selected) return { ...selected, clientSecret: undefined };
          if (propsRef.current.savedCardsOnly) {
            throw new Error(
              "Select a saved company card. To add a card, switch to Personal."
            );
          }
          return elementRef.current.save(context);
        },
      }),
      []
    );

    if (error)
      return (
        <>
          <Alert severity="error">{error}</Alert>
          <Button onClick={props.onRetry}>Retry saved cards</Button>
        </>
      );
    if (!attempt) return <Typography>Loading saved cards…</Typography>;
    return (
      <Elements
        stripe={props.stripePromise}
        options={{
          clientSecret: attempt.clientSecret,
          customerSessionClientSecret:
            attempt.customerSessionClientSecret || undefined,
          appearance: props.appearance,
          fonts: [
            {
              cssSrc:
                "https://fonts.googleapis.com/css?family=Poppins:300,300i,400,500,600",
            },
          ],
        }}
      >
        <SetupElement
          {...props}
          ref={elementRef}
          initialAttempt={attempt}
          checkStatusBeforeConfirm={true}
          onSelectSavedPaymentMethod={(method) => {
            selectedRef.current = method;
            props.onSelectSavedPaymentMethod?.(method);
          }}
          onStateChange={(state) => {
            readyRef.current = state.ready && !state.busy;
            props.onStateChange(state);
          }}
        />
      </Elements>
    );
  }
);
SavedCardSession.displayName = "SavedCardSession";
export default SavedCardSession;
